import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { activity, kpis, revenue } from '../../data/seedData';
import { BarChart, Donut, Sparkline } from '../../components/ui/Charts';
import { Avatar, Badge, Card, MiniStat, Progress, SectionTitle } from '../../components/ui/Primitives';
import { Dialog } from '../../components/ui/Dialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useApp } from '../../lib/app/AppContext';

const comparisons = {
  'Previous month': '-7.4%',
  'Previous quarter': '+12.2%',
  'Previous year': '+18.4%',
} as const;

export function DashboardPage() {
  const { pushToast } = useToast();
  const { user, projects, createWorkspace, annotations, addAnnotation } = useApp();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [comparePeriod, setComparePeriod] = useState<keyof typeof comparisons>('Previous year');
  const firstName = user?.name.split(' ')[0] ?? 'Operator';

  const visibleAnnotations = useMemo(() => annotations.slice(0, 3), [annotations]);

  function exportReport() {
    const payload = JSON.stringify({
      generatedAt: new Date().toISOString(),
      comparison: { period: comparePeriod, change: comparisons[comparePeriod] },
      kpis,
      projects,
      annotations,
    }, null, 2);
    const url = URL.createObjectURL(new Blob([payload], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'gpt-build-report.json';
    anchor.click();
    URL.revokeObjectURL(url);
    pushToast('Report exported', { message: 'Dashboard state was downloaded as JSON.', tone: 'success' });
  }

  function submitAnnotation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const rawText = data.get('annotation');
    if (typeof rawText !== 'string' || !rawText.trim()) return;
    addAnnotation(rawText);
    setAnnotationOpen(false);
    pushToast('Annotation added', { message: rawText.trim(), tone: 'success' });
  }

  function submitWorkspace(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = data.get('workspaceName');
    const region = data.get('region');
    if (typeof name !== 'string' || !name.trim() || typeof region !== 'string') return;
    createWorkspace(name, region);
    setWorkspaceOpen(false);
    pushToast('Workspace created', { message: name.trim(), tone: 'success' });
  }

  return (
    <div className="page-stack">
      <div className="hero-row">
        <div>
          <span className="page-kicker">Executive overview</span>
          <h1>Good evening, {firstName}.</h1>
          <p>Your operating system is healthy. Revenue, retention, and delivery velocity are all trending upward.</p>
        </div>
        <div className="hero-actions">
          <button className="btn secondary" onClick={exportReport}>Export report</button>
          <button className="btn primary" onClick={() => setWorkspaceOpen(true)}>Create workspace</button>
        </div>
      </div>

      <div className="kpi-grid">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className="kpi-card">
            <div className="kpi-top"><span>{kpi.label}</span><Badge tone="positive">{kpi.change}</Badge></div>
            <strong>{kpi.value}</strong>
            <Sparkline values={kpi.spark} />
          </Card>
        ))}
      </div>

      <div className="dashboard-main-grid">
        <Card className="revenue-card">
          <SectionTitle
            eyebrow="Financial performance"
            title="Revenue momentum"
            action={<button className="icon-btn" aria-label="Revenue actions" onClick={() => setMenuOpen((value) => !value)}>•••</button>}
          />
          {menuOpen ? (
            <div className="inline-menu">
              <button onClick={() => { setMenuOpen(false); setCompareOpen(true); }}>Compare period</button>
              <button onClick={() => { setMenuOpen(false); setAnnotationOpen(true); }}>Add annotation</button>
            </div>
          ) : null}
          <div className="chart-summary">
            <div><strong>$428,620</strong><span>September recognized revenue</span></div>
            <div className="summary-metrics">
              <MiniStat label="Forecast" value="$452K" detail="94.8% confidence" />
              <MiniStat label="Target" value="$410K" detail="104.5% attained" />
              <MiniStat label={comparePeriod} value={comparisons[comparePeriod]} detail="relative change" />
            </div>
          </div>
          <BarChart data={revenue} />
          {visibleAnnotations.length ? (
            <div className="annotation-list">
              {visibleAnnotations.map((annotation) => (
                <span key={annotation.id}><i>✎</i>{annotation.text}<small>{new Date(annotation.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small></span>
              ))}
            </div>
          ) : null}
        </Card>

        <Card className="health-card">
          <SectionTitle eyebrow="Platform" title="System health" />
          <div className="donut-row">
            <Donut value={97} label="healthy" sublabel="24 services" />
            <Donut value={91} label="SLO" sublabel="30-day window" />
          </div>
          <div className="health-list">
            <div><span><i className="status-dot ok" /> API Gateway</span><Badge tone="positive">99.99%</Badge></div>
            <div><span><i className="status-dot ok" /> Edge runtime</span><Badge tone="positive">99.97%</Badge></div>
            <div><span><i className="status-dot warn" /> Data pipeline</span><Badge tone="warning">99.82%</Badge></div>
          </div>
        </Card>
      </div>

      <div className="dashboard-lower-grid">
        <Card className="project-table-card">
          <SectionTitle
            eyebrow="Delivery"
            title="Active projects"
            action={<button className="link-button" onClick={() => void navigate('/dashboard/projects')}>View all</button>}
          />
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Project</th><th>Team</th><th>Status</th><th>Health</th><th>Deploys</th><th>Updated</th></tr></thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td><strong>{project.name}</strong></td>
                    <td>{project.owner}</td>
                    <td><Badge tone={project.status === 'Production' ? 'positive' : project.status === 'Staging' ? 'info' : 'warning'}>{project.status}</Badge></td>
                    <td><div className="health-cell"><Progress value={project.health} /><span>{project.health}%</span></div></td>
                    <td>{project.deploys}</td>
                    <td>{project.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <SectionTitle eyebrow="Collaboration" title="Live activity" />
          <div className="activity-list">
            {activity.map((item) => (
              <div className="activity-item" key={item.actor + item.time}>
                <Avatar name={item.actor} size="sm" />
                <div><p><strong>{item.actor}</strong> {item.action} <b>{item.target}</b></p><span>{item.time} ago</span></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Dialog open={workspaceOpen} title="Create workspace" onClose={() => setWorkspaceOpen(false)}>
        <form className="form-stack" onSubmit={submitWorkspace}>
          <label>Workspace name<input name="workspaceName" required /></label>
          <label>Region<select name="region"><option>Canada Central</option><option>US East</option></select></label>
          <button className="btn primary" type="submit">Create workspace</button>
        </form>
      </Dialog>

      <Dialog open={compareOpen} title="Compare revenue period" onClose={() => setCompareOpen(false)}>
        <div className="form-stack">
          <label>Comparison period
            <select value={comparePeriod} onChange={(event) => setComparePeriod(event.target.value as keyof typeof comparisons)}>
              {Object.keys(comparisons).map((period) => <option key={period}>{period}</option>)}
            </select>
          </label>
          <div className="comparison-preview">
            <span>{comparePeriod}</span><strong>{comparisons[comparePeriod]}</strong>
          </div>
          <button className="btn primary" onClick={() => { setCompareOpen(false); pushToast('Comparison updated', { message: comparePeriod, tone: 'info' }); }}>Apply comparison</button>
        </div>
      </Dialog>

      <Dialog open={annotationOpen} title="Add chart annotation" onClose={() => setAnnotationOpen(false)}>
        <form className="form-stack" onSubmit={submitAnnotation}>
          <label>Annotation<textarea name="annotation" rows={4} required placeholder="Record context for this revenue period…" /></label>
          <button className="btn primary" type="submit">Add annotation</button>
        </form>
      </Dialog>
    </div>
  );
}
